/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {useCallback, useState} from 'react';

import {vidyoProviderLogs} from '@/utils/general';

import {registerFilter, unregisterFilter} from '../utils';

export const useMediaControls = () => {
    const [isCameraOn, setIsCameraOn] = useState(true);
    const [isMicrophoneOn, setIsMicrophoneOn] = useState(true);
    const [isSpeakerOn, setIsSpeakerOn] = useState(true);
    const [bgNoiseOn, setBgNoiseOn] = useState(false);

    /**
     * Passed to useVidyoConnection so it can signal when bg noise is
     * initialised on a new connection (dependency injection).
     */
    const onBgNoiseInit = useCallback((enabled: boolean) => {
        setBgNoiseOn(enabled);
    }, []);

    const handleCameraPrivacy = useCallback(() => {
        if (isCameraOn) {
            window?.vidyoConnector.SetCameraPrivacy({privacy: true});
            setIsCameraOn(false);
        } else {
            window?.vidyoConnector.SetCameraPrivacy({privacy: false});
            setIsCameraOn(true);
        }
    }, [isCameraOn]);

    const handleMicrophonePrivacy = useCallback(() => {
        if (isMicrophoneOn) {
            window?.vidyoConnector.SetMicrophonePrivacy({privacy: true});
            setIsMicrophoneOn(false);
        } else {
            window?.vidyoConnector.SetMicrophonePrivacy({privacy: false});
            setIsMicrophoneOn(true);
        }
    }, [isMicrophoneOn]);

    const handleSpeakerPrivacy = useCallback(() => {
        if (isSpeakerOn) {
            window?.vidyoConnector.SetSpeakerPrivacy({privacy: true});
            setIsSpeakerOn(false);
        } else {
            window?.vidyoConnector.SetSpeakerPrivacy({privacy: false});
            setIsSpeakerOn(true);
        }
    }, [isSpeakerOn]);

    const handleBackgroundNoise = useCallback(async () => {
        if (!bgNoiseOn) {
            vidyoProviderLogs({
                message: 'Enabling BACKGROUND NOISE CANCELLATION',
                eventType: 'stream-effect',
                status: 'info',
            });
            setBgNoiseOn(true);
            setTimeout(() => {
                registerFilter('bg_noise_cancellation');
            }, 0);
        } else {
            vidyoProviderLogs({
                message: 'Disabling BACKGROUND NOISE CANCELLATION',
                eventType: 'stream-effect',
                status: 'info',
            });
            setBgNoiseOn(false);
            unregisterFilter('audio');
        }
    }, [bgNoiseOn]);

    const restartCamera = useCallback(() => {
        vidyoProviderLogs({
            message: 'Permission granted — restarting camera device',
            eventType: 'device',
            status: 'info',
        });
        window?.vidyoConnector.SetCameraPrivacy({privacy: true});
        setTimeout(() => {
            window?.vidyoConnector.SetCameraPrivacy({privacy: false});
            setIsCameraOn(true);
        }, 300);
    }, []);

    const restartMicrophone = useCallback(() => {
        vidyoProviderLogs({
            message: 'Permission granted — restarting microphone device',
            eventType: 'device',
            status: 'info',
        });
        window?.vidyoConnector.SetMicrophonePrivacy({privacy: true});
        setTimeout(() => {
            window?.vidyoConnector.SetMicrophonePrivacy({privacy: false});
            setIsMicrophoneOn(true);
        }, 300);
    }, []);

    const handleScreenShare = useCallback(async () => {
        if (!window.localWindowShare) {
            vidyoProviderLogs({
                message: 'No localWindowShare available',
                eventType: 'features',
                status: 'error',
            });
            return;
        }

        window?.localWindowShare.SetPreviewLabel({previewLabel: 'My Share'});
        if (window?.localWindowShare.SetEndShareLabel) {
            window?.localWindowShare.SetEndShareLabel({endLabel: 'End Share'});
        }

        window?.vidyoConnector.SelectLocalWindowShare({localWindowShare: null});
        await window?.vidyoConnector.ShowWindowSharePreview({preview: false});

        try {
            await window?.vidyoConnector.SelectLocalWindowShare({
                localWindowShare: window.localWindowShare,
            });
            await window?.vidyoConnector.ShowWindowSharePreview({preview: true});
        } catch {
            vidyoProviderLogs({
                message: 'Screen share canceled or failed',
                eventType: 'features',
                status: 'info',
            });
        }
    }, []);

    return {
        isCameraOn,
        isMicrophoneOn,
        isSpeakerOn,
        bgNoiseOn,
        onBgNoiseInit,
        handleCameraPrivacy,
        handleMicrophonePrivacy,
        handleSpeakerPrivacy,
        handleBackgroundNoise,
        handleScreenShare,
        restartCamera,
        restartMicrophone,
    };
};
